import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Search, Eye, Package, Loader2, Truck, X } from 'lucide-react';
import { ordersAPI } from '../../services/api';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const statuses = ['all', 'new', 'paid', 'shipped', 'delivered', 'refunded'];

  useEffect(() => {
    let isMounted = true;
    
    const fetchOrders = async () => {
      try {
        const params = statusFilter !== 'all' ? { status: statusFilter } : {};
        const response = await ordersAPI.getAll(params);
        if (isMounted) {
          setOrders(response.data || []);
        }
      } catch (error) {
        if (isMounted && error.name !== 'CanceledError') {
          console.error('Error fetching orders:', error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    fetchOrders();
    
    return () => {
      isMounted = false;
    };
  }, [statusFilter]);

  const filteredOrders = orders.filter(order => {
    const orderNumber = order.order_number || '';
    const customerName = order.shipping_address?.first_name + ' ' + order.shipping_address?.last_name || '';
    const matchesSearch = 
      orderNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customerName.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setTrackingNumber(order.tracking_number || '');
    setShowModal(true);
  };

  const handleUpdateStatus = async (newStatus) => {
    if (!selectedOrder) return;
    
    setUpdatingStatus(true);
    try {
      await ordersAPI.updateStatus(selectedOrder._id, newStatus, trackingNumber || null);
      
      // Update local state
      setOrders(orders.map(o => 
        o._id === selectedOrder._id 
          ? { ...o, status: newStatus, tracking_number: trackingNumber || o.tracking_number }
          : o
      ));
      setSelectedOrder({ ...selectedOrder, status: newStatus, tracking_number: trackingNumber });
      alert('Order status updated!');
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Failed to update order status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'new':
        return 'bg-soft-terracotta/10 text-soft-terracotta';
      case 'paid':
        return 'bg-muted-green/10 text-muted-green';
      case 'shipped':
        return 'bg-blue-100 text-blue-700';
      case 'delivered':
        return 'bg-green-100 text-green-700';
      case 'refunded':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-soft-sand text-charcoal';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-charcoal font-heading">Orders</h1>
            <p className="text-stone-grey font-body">{orders.length} total orders</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg p-6 border border-soft-sand shadow-soft">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-grey" />
              <Input
                type="text"
                placeholder="Search orders..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 font-body"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-soft-sand rounded bg-white text-charcoal font-body focus:border-muted-green focus:ring-muted-green"
            >
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status === 'all' ? 'All Statuses' : status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-lg border border-soft-sand shadow-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-soft-sand">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Order ID</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Customer</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Items</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Total</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-charcoal font-body">Tracking</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-charcoal font-body">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-soft-sand">
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-stone-grey font-body">
                      {orders.length === 0 ? 'No orders yet.' : 'No orders match your search.'}
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => (
                    <tr key={order._id} className="hover:bg-soft-sand/30 transition-colors">
                      <td className="px-6 py-4 font-medium text-charcoal font-body">{order.order_number}</td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-charcoal font-body">
                            {order.shipping_address?.first_name} {order.shipping_address?.last_name}
                          </p>
                          <p className="text-sm text-stone-grey font-body">{order.shipping_address?.city}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-stone-grey font-body">{formatDate(order.created_at)}</td>
                      <td className="px-6 py-4 text-sm text-stone-grey font-body">{order.items?.length || 0}</td>
                      <td className="px-6 py-4 font-medium text-charcoal font-body">${order.total?.toFixed(2)}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded text-sm font-medium ${getStatusColor(order.status)} font-body capitalize`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-stone-grey font-body">
                        {order.tracking_number || '-'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-charcoal hover:bg-soft-sand"
                            onClick={() => handleViewOrder(order)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Order Detail Modal */}
      {showModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl">
            <div className="p-6 border-b border-soft-sand flex items-center justify-between">
              <h2 className="text-2xl font-bold text-charcoal font-heading">
                Order {selectedOrder.order_number}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-stone-grey hover:text-charcoal">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2 font-body">Order Status</label>
                <div className="flex flex-wrap gap-2">
                  {['new', 'paid', 'shipped', 'delivered', 'refunded'].map((status) => (
                    <Button
                      key={status}
                      variant={selectedOrder.status === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleUpdateStatus(status)}
                      disabled={updatingStatus}
                      className={selectedOrder.status === status ? 'bg-muted-green hover:bg-muted-green/90 text-white' : ''}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Tracking Number */}
              <div>
                <label className="block text-sm font-medium text-charcoal mb-2 font-body">Tracking Number</label>
                <div className="flex gap-2">
                  <Input
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Enter tracking number"
                    className="font-body"
                  />
                  <Button 
                    onClick={() => handleUpdateStatus(selectedOrder.status)}
                    disabled={updatingStatus}
                    className="bg-muted-green hover:bg-muted-green/90 text-white"
                  >
                    <Truck className="h-4 w-4 mr-2" />
                    Save
                  </Button>
                </div>
              </div>

              {/* Customer Info */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-charcoal mb-2 font-body">Shipping Address</h3>
                  <div className="text-sm text-stone-grey font-body space-y-1">
                    <p>{selectedOrder.shipping_address?.first_name} {selectedOrder.shipping_address?.last_name}</p>
                    <p>{selectedOrder.shipping_address?.address}</p>
                    <p>{selectedOrder.shipping_address?.city}, {selectedOrder.shipping_address?.state} {selectedOrder.shipping_address?.zip}</p>
                    <p>{selectedOrder.shipping_address?.phone}</p>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-charcoal mb-2 font-body">Order Summary</h3>
                  <div className="text-sm text-stone-grey font-body space-y-1">
                    <p>Subtotal: ${selectedOrder.subtotal?.toFixed(2)}</p>
                    <p>Shipping: ${selectedOrder.shipping_cost?.toFixed(2)}</p>
                    <p className="font-bold text-charcoal">Total: ${selectedOrder.total?.toFixed(2)}</p>
                    <p>Payment: {selectedOrder.payment_method}</p>
                  </div>
                </div>
              </div>

              {/* Items */}
              <div>
                <h3 className="font-semibold text-charcoal mb-3 font-body">Items ({selectedOrder.items?.length || 0})</h3>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-soft-sand/30 rounded">
                      <div>
                        <p className="font-medium text-charcoal font-body">{item.product_name}</p>
                        <p className="text-sm text-stone-grey font-body">Qty: {item.quantity} × ${item.price?.toFixed(2)}</p>
                      </div>
                      <p className="font-bold text-charcoal font-body">${item.total?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminOrders;
